import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { BookOpen, Code2, Download, Menu, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { guideDocs, exampleDocs, postmanCollectionUrl, postmanStagingUrl, postmanProductionUrl } from '../manifest'
import type { DocEntry } from '../types'

function docPath(doc: DocEntry): string {
  if (doc.slug === 'readme') return '/developers/onboarding'
  if (doc.isExample) return `/developers/onboarding/examples/${doc.slug}`
  return `/developers/onboarding/${doc.slug}`
}

interface NavItemProps {
  to: string
  children: React.ReactNode
  onClick?: () => void
}

function NavItem({ to, children, onClick }: NavItemProps) {
  return (
    <NavLink
      to={to}
      end={to === '/developers/onboarding'}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'block rounded px-3 py-1.5 text-sm transition-colors',
          'hover:bg-muted hover:text-fg',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
          isActive
            ? 'bg-[color:var(--brand-primary,hsl(var(--primary)))]/10 font-medium text-[color:var(--brand-primary,hsl(var(--primary)))]'
            : 'text-muted-fg',
        )
      }
    >
      {children}
    </NavLink>
  )
}

interface SidebarContentProps {
  onLinkClick?: () => void
}

function SidebarContent({ onLinkClick }: SidebarContentProps) {
  return (
    <nav aria-label="Documentation navigation" className="flex flex-col gap-6 px-2 py-4">
      {/* Guide group */}
      <section>
        <div className="mb-2 flex items-center gap-2 px-3">
          <BookOpen size={13} aria-hidden className="text-muted-fg" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-fg">
            Guide
          </span>
        </div>
        <ul className="flex flex-col gap-0.5">
          {guideDocs.map((doc) => (
            <li key={doc.slug}>
              <NavItem to={docPath(doc)} onClick={onLinkClick}>
                {doc.title}
              </NavItem>
            </li>
          ))}
        </ul>
      </section>

      {/* Examples group */}
      <section>
        <div className="mb-2 flex items-center gap-2 px-3">
          <Code2 size={13} aria-hidden className="text-muted-fg" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-fg">
            Code Examples
          </span>
        </div>
        <ul className="flex flex-col gap-0.5">
          {exampleDocs.map((doc) => (
            <li key={doc.slug}>
              <NavItem to={docPath(doc)} onClick={onLinkClick}>
                {doc.title}
              </NavItem>
            </li>
          ))}
        </ul>
      </section>

      {/* Resources group */}
      <section>
        <div className="mb-2 flex items-center gap-2 px-3">
          <Download size={13} aria-hidden className="text-muted-fg" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-fg">
            Resources
          </span>
        </div>
        <ul className="flex flex-col gap-0.5">
          <li>
            <a
              href={postmanCollectionUrl}
              download="antifraud-rest-api.postman_collection.json"
              data-testid="download-collection"
              className={cn(
                'block rounded px-3 py-1.5 text-sm text-muted-fg transition-colors',
                'hover:bg-muted hover:text-fg',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
              )}
              onClick={onLinkClick}
            >
              Postman Collection
            </a>
          </li>
          <li>
            <a
              href={postmanStagingUrl}
              download="antifraud-staging.postman_environment.json"
              className={cn(
                'block rounded px-3 py-1.5 text-sm text-muted-fg transition-colors',
                'hover:bg-muted hover:text-fg',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
              )}
              onClick={onLinkClick}
            >
              Staging Environment
            </a>
          </li>
          <li>
            <a
              href={postmanProductionUrl}
              download="antifraud-production.postman_environment.json"
              className={cn(
                'block rounded px-3 py-1.5 text-sm text-muted-fg transition-colors',
                'hover:bg-muted hover:text-fg',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
              )}
              onClick={onLinkClick}
            >
              Production Environment
            </a>
          </li>
        </ul>
      </section>
    </nav>
  )
}

interface OnboardingSidebarProps {
  className?: string
}

export function OnboardingSidebar({ className }: OnboardingSidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <>
      {/* Mobile hamburger trigger */}
      <div className="sticky top-0 z-30 flex items-center gap-2 border-b border-border bg-bg px-4 py-3 lg:hidden">
        <button
          type="button"
          aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={mobileOpen}
          aria-controls="mobile-sidebar"
          onClick={() => setMobileOpen((v) => !v)}
          className="rounded p-1.5 text-muted-fg hover:bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {mobileOpen ? <X size={18} aria-hidden /> : <Menu size={18} aria-hidden />}
        </button>
        <span className="text-sm font-medium text-fg">Navigation</span>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          id="mobile-sidebar"
          className="fixed inset-0 z-40 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Documentation navigation"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          {/* Drawer panel */}
          <div className="absolute left-0 top-0 h-full w-72 overflow-y-auto bg-bg shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="text-sm font-semibold text-fg">Documentation</span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation"
                className="rounded p-1.5 text-muted-fg hover:bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <X size={16} aria-hidden />
              </button>
            </div>
            <SidebarContent onLinkClick={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Desktop sidebar — sticky, full viewport height */}
      <aside
        className={cn(
          'hidden lg:block lg:sticky lg:top-0 lg:h-screen lg:w-64 lg:shrink-0 lg:overflow-y-auto',
          'border-r border-border bg-bg',
          className,
        )}
        aria-label="Documentation navigation"
      >
        <SidebarContent />
      </aside>
    </>
  )
}
